import { useState } from 'react';
import { useTranslations } from '@/i18n/use-translations.tsx';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CloseIcon from '@mui/icons-material/Close';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import type { Mode } from '@/types/entity-mode';
import Widget from '@/components/common/widget';
import { AddSessionContributorList } from './add-session-contributor-list';
import { SessionContributorList } from './session-contributor-list';
import { IgnorableClickAwayListener } from '@/components/common/ignorable-click-away-listener.tsx';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { useGetSessionQuery } from '@/state/queries/sessions/use-get-session';
import { pickNewerSession } from '@/components/flows/detail/flow-detail.utils';
import type { ContributorListDisplayVariant } from '@/components/flows/detail/flow-detail.types';
import { getObjectId } from '@/helpers/object.id.helper';

export const SessionActors = ({
  session,
  modeOverride,
  displayVariant,
}: {
  session: SessionDto;
  modeOverride?: Mode;
  displayVariant?: ContributorListDisplayVariant;
}) => {
  const t = useTranslations();
  const [showAddActorInput, setShowAddActorInput] = useState(false);
  const eventMode = useEventMode();
  const mode = modeOverride ?? eventMode;

  // Get fresh session data to ensure we have up-to-date contributors list
  // Use origin field for mode-based session lookup
  // Map mode to backend mode: PLAN -> PLAN, ORGANIZE/PERFORM -> ORGANIZE
  const backendMode = mode === 'PLAN' ? 'PLAN' : mode === 'ORGANIZE' || mode === 'PERFORM' ? 'ORGANIZE' : undefined;
  const sessionOriginId = session.origin ? getObjectId(session.origin) : session.id;
  const { data: freshSession } = useGetSessionQuery(sessionOriginId, backendMode);
  const currentSession = pickNewerSession(session, freshSession) ?? session;

  return (
    <Widget
      title={`${currentSession.contributors?.length || 0} ${t('common.actors')}`}
      primaryButton={{
        label: showAddActorInput ? t('common.cancel') : t('common.add'),
        disabled: mode === 'PERFORM',
        onClick: () => setShowAddActorInput((prev) => !prev),
        startIcon: showAddActorInput ? <CloseIcon /> : <AddOutlinedIcon />,
        testId: 'add-session-actor-button',
        id: 'add-session-actor-button',
      }}
    >
      {showAddActorInput && (
        <IgnorableClickAwayListener onClickAway={() => setShowAddActorInput(false)} ignoreId="add-session-actor-button">
          <div>
            <AddSessionContributorList
              session={currentSession}
              modeOverride={modeOverride}
              enableFlowSlotContext={displayVariant === 'flowSlotAssets' || displayVariant === 'flowScheduleInline'}
              onSuccess={() => setShowAddActorInput(false)}
            />
          </div>
        </IgnorableClickAwayListener>
      )}
      <SessionContributorList session={currentSession} displayVariant={displayVariant} />
    </Widget>
  );
};
